/**
 * Data hook for the org structure browser: loads units, builds the tree,
 * drives search filtering and expansion state.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import type { Temporal } from '@insio/dates';
import { OrgUnitsDocument, type OrgUnitsQuery } from '@/graphql';
import { useToday } from '@/hooks/use-today';
import { LifecycleStatusFilter, matchesLifecycleFilter } from '@/utils/lifecycle-status';
import { missingAtSourceBySystem } from '@/utils/sync-missing';

export type OrgUnitNode = OrgUnitsQuery['orgUnits'][number];

/** The unit fields the tree reads; a lighter query can feed it too. */
export type OrgTreeUnit = Pick<
  OrgUnitNode,
  | 'id'
  | 'code'
  | 'name'
  | 'parentId'
  | 'sortOrder'
  | 'memberCount'
  | 'isActive'
  | 'validFrom'
  | 'validTo'
> & { manager: { displayName: string } | null };

export interface OrgTreeNode<TUnit extends OrgTreeUnit = OrgUnitNode> {
  unit: TUnit;
  children: OrgTreeNode<TUnit>[];
  /** Members a search found the unit by, when its code and name did not match. */
  matchedMembers?: string[];
}

/** Builds the unit hierarchy; units whose parent is missing become roots. */
export function buildOrgTree<TUnit extends OrgTreeUnit>(
  units: readonly TUnit[]
): OrgTreeNode<TUnit>[] {
  const byId = new Map(units.map(unit => [unit.id, unit]));
  const childrenOf = new Map<string | null, TUnit[]>();

  for (const unit of units) {
    const parentKey = unit.parentId && byId.has(unit.parentId) ? unit.parentId : null;
    const siblings = childrenOf.get(parentKey) ?? [];
    siblings.push(unit);
    childrenOf.set(parentKey, siblings);
  }

  const sortSiblings = (siblings: TUnit[]) =>
    siblings.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

  const toNode = (unit: TUnit): OrgTreeNode<TUnit> => ({
    unit,
    children: sortSiblings(childrenOf.get(unit.id) ?? []).map(toNode)
  });

  return sortSiblings(childrenOf.get(null) ?? []).map(toNode);
}

/**
 * Prunes the tree to units matching the search (code, name, member), status and `missingOnly`,
 * keeping their ancestors; a unit found only by a member carries the matched names.
 */
export function filterOrgTree(
  nodes: OrgTreeNode[],
  search: string,
  status: LifecycleStatusFilter,
  today: Temporal.PlainDate,
  missingOnly = false
): OrgTreeNode[] {
  const query = search.trim().toLowerCase();
  if (!query && status === LifecycleStatusFilter.ALL && !missingOnly) return nodes;

  const passesFilters = (unit: OrgUnitNode) =>
    matchesLifecycleFilter(unit, status, today) &&
    (!missingOnly || missingAtSourceBySystem(unit.syncProvenance).length > 0);
  const matchesUnit = (unit: OrgUnitNode) =>
    !query || unit.name.toLowerCase().includes(query) || unit.code.toLowerCase().includes(query);
  const membersMatching = (unit: OrgUnitNode) =>
    unit.members
      .filter(
        member =>
          member.displayName.toLowerCase().includes(query) ||
          (member.email ?? '').toLowerCase().includes(query)
      )
      .map(member => member.displayName || (member.email ?? ''));

  const prune = (node: OrgTreeNode): OrgTreeNode | null => {
    const children = node.children.map(prune).filter((child): child is OrgTreeNode => !!child);
    if (passesFilters(node.unit)) {
      if (matchesUnit(node.unit)) return { ...node, children };
      const matchedMembers = membersMatching(node.unit);
      if (matchedMembers.length > 0) return { ...node, children, matchedMembers };
    }
    if (children.length > 0) return { ...node, children };
    return null;
  };

  return nodes.map(prune).filter((node): node is OrgTreeNode => !!node);
}

/** Ids of every node that has children — used to expand the whole tree. */
export function collectExpandableIds(
  nodes: OrgTreeNode<OrgTreeUnit>[],
  into: Set<string> = new Set()
): Set<string> {
  for (const node of nodes) {
    if (node.children.length > 0) into.add(node.unit.id);
    collectExpandableIds(node.children, into);
  }
  return into;
}

export function useOrgStructure() {
  const { data, loading, error } = useQuery(OrgUnitsDocument);
  const units = useMemo(() => data?.orgUnits ?? [], [data?.orgUnits]);

  const tree = useMemo(() => buildOrgTree(units), [units]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LifecycleStatusFilter>(
    LifecycleStatusFilter.ALL
  );
  const [missingOnly, setMissingOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Expand everything and open the first root once data first arrives — small trees
  // read best open, and an empty detail panel next to a populated tree looks broken.
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current || tree.length === 0) return;
    initialized.current = true;
    setExpandedIds(collectExpandableIds(tree));
    setSelectedId(current => current ?? tree[0].unit.id);
  }, [tree]);

  const today = useToday();
  const filteredTree = useMemo(
    () => filterOrgTree(tree, search, statusFilter, today, missingOnly),
    [tree, search, statusFilter, today, missingOnly]
  );

  // While filtering, the path to every match stays visible regardless of manual collapse.
  const searchActive =
    search.trim().length > 0 || statusFilter !== LifecycleStatusFilter.ALL || missingOnly;
  const visibleExpandedIds = useMemo(
    () => (searchActive ? collectExpandableIds(filteredTree) : expandedIds),
    [searchActive, filteredTree, expandedIds]
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const unitsById = useMemo(() => new Map(units.map(unit => [unit.id, unit])), [units]);

  // A unit saved just now may not be loaded yet; it is revealed once it arrives.
  const [revealId, setRevealId] = useState<string | null>(null);
  useEffect(() => {
    const target = revealId ? unitsById.get(revealId) : undefined;
    if (!target) return;
    setExpandedIds(prev => {
      const next = new Set(prev);
      let parentId = target.parentId;
      while (parentId && !next.has(parentId)) {
        next.add(parentId);
        parentId = unitsById.get(parentId)?.parentId ?? null;
      }
      return next;
    });
    setSelectedId(target.id);
    setRevealId(null);
  }, [revealId, unitsById]);
  const selectedUnit = selectedId ? (unitsById.get(selectedId) ?? null) : null;

  const childrenOfSelected = useMemo(() => {
    if (!selectedId) return [];
    return units
      .filter(unit => unit.parentId === selectedId)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }, [units, selectedId]);

  const parentOfSelected =
    selectedUnit?.parentId != null ? (unitsById.get(selectedUnit.parentId) ?? null) : null;

  return {
    loading,
    error,
    isEmpty: !loading && !error && units.length === 0,
    units,
    tree: filteredTree,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    missingOnly,
    setMissingOnly,
    selectedId,
    setSelectedId,
    revealUnit: setRevealId,
    selectedUnit,
    parentOfSelected,
    childrenOfSelected,
    searchActive,
    expandedIds: visibleExpandedIds,
    toggleExpanded
  };
}
